import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Calendar, ChevronLeft, ChevronRight, AlertTriangle, Settings2, Plus, X, Clock, BookOpen, AlertCircle, BarChart3, CheckSquare, ListTodo, GraduationCap, MapPin } from "lucide-react";
import { ApiService } from "../services/api";
import { useTheme } from "../contexts/ThemeContext";
import checkPermission from "../utils/permission";
import { isYesToolsHostname } from "../utils/yesTools";
import { defaultMenuItems } from "../sidebar/data/defaultMenuItems";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import remarkBreaks from "remark-breaks";
import Barcode from "react-barcode";

// ─── Widget type definitions ─────────────────────────────────────────────────
type DashboardWidgetDefinition = {
  id: string;
  label: string;
  icon: any;
  color: string;
  description: string;
  relatedMenuItemIds?: string[];
  requiredPermissions?: Array<string | string[]>;
};

export const WIDGET_DEFINITIONS: DashboardWidgetDefinition[] = [
  {
    id: "last_missing",
    label: "Son Eksikler",
    icon: AlertCircle,
    color: "#fb7185",
    description: "Son eksiklerinizi listeler",
    relatedMenuItemIds: ["lessons"],
  },
  {
    id: "missing_summary",
    label: "Eksik Özeti",
    icon: BarChart3,
    color: "#fdba74",
    description: "Eksiklerinizin özetini gösterir",
    relatedMenuItemIds: ["lessons"],
  },
  {
    id: "error_booklet",
    label: "Hata Kitapçığı",
    icon: BookOpen,
    color: "#34d399",
    description: "Hata kitapçığı durumunuzu gösterir",
    relatedMenuItemIds: ["lessons"],
  },
  {
    id: "upcoming_homework",
    label: "Yaklaşan Ödevler",
    icon: CheckSquare,
    color: "#60a5fa",
    description: "Yaklaşan ödevlerinizi listeler",
    relatedMenuItemIds: ["calendar"],
  },
  {
    id: "upcoming_meetings",
    label: "Yaklaşan Toplantılar",
    icon: Calendar,
    color: "#a78bfa",
    description: "Yaklaşan toplantılarınızı gösterir",
    relatedMenuItemIds: ["meetings"],
  },
  {
    id: "upcoming_appointments",
    label: "Yaklaşan Randevular",
    icon: Clock,
    color: "#f472b6",
    description: "Yaklaşan randevularınızı gösterir",
    relatedMenuItemIds: ["appointments"],
  },
  {
    id: "upcoming_events",
    label: "Yaklaşan Etkinlikler",
    icon: Calendar,
    color: "#fb923c",
    description: "Yaklaşan etkinliklerinizi gösterir",
    relatedMenuItemIds: ["calendar"],
  },
  {
    id: "daily_routine",
    label: "Günlük Rutin",
    icon: ListTodo,
    color: "#4ade80",
    description: "Günlük rutinlerinizi listeler",
    relatedMenuItemIds: ["todos"],
    requiredPermissions: ["todo_daily_routine_list"],
  },
  {
    id: "tasks",
    label: "Görevler",
    icon: CheckSquare,
    color: "#facc15",
    description: "Yapılacaklar alanındaki görevlerinizi listeler",
    relatedMenuItemIds: ["todos"],
    requiredPermissions: ["todo_item_view"],
  },
  {
    id: "exam_countdown",
    label: "Sınava Ne Kadar Kaldı",
    icon: GraduationCap,
    color: "#f43f5e",
    description: "Sınavlarınıza ne kadar kaldığını gösterir",
  },
] as const;

type WidgetId = typeof WIDGET_DEFINITIONS[number]["id"];

const MAX_WIDGETS = 5;
const WIDGET_ID_SET = new Set<string>(WIDGET_DEFINITIONS.map((widget) => widget.id));
const MENU_ITEM_PERMISSION_MAP = new Map(defaultMenuItems.map((item) => [item.id, item.permission]));

const isWidgetId = (value: string): value is WidgetId => WIDGET_ID_SET.has(value);

const normalizeWidgetIds = (widgets: unknown): WidgetId[] => {
  if (!Array.isArray(widgets)) return [];

  return widgets
    .filter((item): item is WidgetId => typeof item === "string" && isWidgetId(item))
    .filter((item, index, arr) => arr.indexOf(item) === index)
    .slice(0, MAX_WIDGETS);
};

const getDefaultWidgetIds = (availableWidgetIds: Set<WidgetId>): WidgetId[] => {
  if (availableWidgetIds.has("exam_countdown")) {
    return ["exam_countdown"];
  }

  return [];
};

// ─── Widget content components ────────────────────────────────────────────────

const WidgetLastMissing: React.FC<{ theme: any }> = ({ theme }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await ApiService.getUserGains({ isResolved: false, limit: 3, sortBy: "createdAt", sortOrder: "DESC" });
        setData(Array.isArray(res?.data) ? res.data.slice(0, 3) : []);
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <WidgetLoading />;
  if (!data.length) return <WidgetEmpty text="Son eksik bulunamadı" />;

  return (
    <div className="space-y-2">
      {data.map((item: any, i: number) => (
        <div key={i} className="flex items-center gap-3 rounded-lg p-2" style={{ background: `${theme.colors.primary}15` }}>
          <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: "#fb7185" }} />
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{item.gainText || item.content?.title || "Eksik"}</p>
            <p className="text-gray-400 text-xs truncate">
              {[item.lesson?.title, item.topic?.title].filter(Boolean).join(" • ") || "Ders bilgisi yok"}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

const WidgetMissingSummary: React.FC<{ theme: any }> = ({ theme }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await ApiService.getGainReports();
        const total = Number(res?.summary?.totalGains || 0);
        const resolved = Number(res?.summary?.resolvedGains || 0);
        setData({
          total,
          resolved,
        });
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <WidgetLoading />;
  if (!data) return <WidgetEmpty text="Eksik özeti yüklenemedi" />;

  const total = data.total || 0;
  const resolved = data.resolved || 0;
  const percent = total > 0 ? Math.round((resolved / total) * 100) : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-gray-400 text-sm">Toplam Eksik</span>
        <span className="text-white font-bold text-lg">{total}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-400 text-sm">Çözülen</span>
        <span className="font-bold text-lg" style={{ color: "#34d399" }}>{resolved}</span>
      </div>
      <div className="w-full rounded-full h-2" style={{ background: `${theme.colors.primary}30` }}>
        <div
          className="h-2 rounded-full transition-all duration-500"
          style={{ width: `${percent}%`, background: `linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.accent})` }}
        />
      </div>
      <p className="text-center text-xs text-gray-400">%{percent} tamamlandı</p>
    </div>
  );
};

const WidgetErrorBooklet: React.FC<{ theme: any }> = ({ theme }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const latestBooklet = await ApiService.getMyErrorBooklets({ page: 1, limit: 1 });
        const booklet = Array.isArray(latestBooklet?.data) ? latestBooklet.data[0] || null : null;
        setData(booklet);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <WidgetLoading />;
  if (!data?.id) return <WidgetEmpty text="Hata kitapçığı bulunamadı" />;

  const booklet = data;
  const monthLabel = typeof booklet.month === "number" && typeof booklet.year === "number"
    ? new Date(booklet.year, booklet.month - 1, 1).toLocaleDateString("tr-TR", { month: "long", year: "numeric" })
    : null;

  return (
    <button
      type="button"
      onClick={() => navigate(`/lessons?tab=error-booklet&bookletId=${booklet.id}`)}
      className="w-full space-y-2 text-left rounded-xl p-2 -m-2 transition-colors hover:bg-white/5"
      title="Hata kitapçığını aç"
    >
      {monthLabel ? (
        <div className="flex items-center justify-between">
          <span className="text-gray-400 text-sm">Dönem</span>
          <span className="text-white text-sm font-medium capitalize">{monthLabel}</span>
        </div>
      ) : null}
      <div className="flex items-center justify-between">
        <span className="text-gray-400 text-sm">Durum</span>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{
          background: booklet.status === "COMPLETED" ? "#34d39920" : `${theme.colors.primary}20`,
          color: booklet.status === "COMPLETED" ? "#34d399" : theme.colors.accent
        }}>
          {booklet.status === "COMPLETED" ? "Tamamlandı" : booklet.status === "IN_PROGRESS" ? "Devam Ediyor" : "Bekliyor"}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-400 text-sm">Toplam Soru</span>
        <span className="text-white font-bold">{booklet.totalQuestions || 0}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-400 text-sm">Başarı Oranı</span>
        <span className="font-bold" style={{ color: theme.colors.accent }}>
          %{booklet.totalQuestions ? Math.round(((booklet.correctAnswers || 0) / booklet.totalQuestions) * 100) : 0}
        </span>
      </div>
      <div className="pt-1 text-xs font-semibold" style={{ color: theme.colors.accent }}>
        Kitapçığı Aç
      </div>
    </button>
  );
};

const WidgetCalendar: React.FC<{ theme: any; types: string[]; label: string }> = ({ theme, types, label }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const typesKey = types.join(",");

  useEffect(() => {
    const load = async () => {
      try {
        const now = new Date();
        const res = await ApiService.getAllCalendars({
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          types: typesKey,
          limit: 5,
        });
        const rows = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = rows.filter((r: any) => {
          const d = new Date(r.startDate || r.date || r.createdAt);
          return d >= today;
        }).slice(0, 3);
        setData(upcoming);
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [types, typesKey]);

  if (loading) return <WidgetLoading />;
  if (!data.length) return <WidgetEmpty text={`Yaklaşan ${label.toLowerCase()} bulunamadı`} />;

  return (
    <div className="space-y-2">
      {data.map((item: any, i: number) => {
        const dateStr = item.startDate || item.date || item.createdAt;
        const d = dateStr ? new Date(dateStr) : null;
        return (
          <div key={i} className="flex items-start gap-3 rounded-lg p-2" style={{ background: `${theme.colors.primary}15` }}>
            <div className="flex-shrink-0 text-center rounded-lg p-1.5 min-w-[36px]" style={{ background: `${theme.colors.accent}20` }}>
              {d ? (
                <>
                  <div className="text-xs font-bold leading-none" style={{ color: theme.colors.accent }}>{d.getDate()}</div>
                  <div className="text-xs text-gray-400 leading-none">{d.toLocaleDateString("tr-TR", { month: "short" })}</div>
                </>
              ) : <Calendar className="w-4 h-4 text-gray-400" />}
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{item.title || item.name || label}</p>
              {item.description && <p className="text-gray-400 text-xs truncate">{item.description}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const WidgetTodos: React.FC<{ theme: any; type: "daily" | "tasks" }> = ({ theme, type }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingRoutineIds, setTogglingRoutineIds] = useState<string[]>([]);
  const [togglingTaskIds, setTogglingTaskIds] = useState<string[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        if (type === "daily") {
          const res = await ApiService.getDailyRoutines({ limit: 5, sortBy: "order", sortOrder: "ASC" });
          setData(Array.isArray(res?.data) ? res.data.slice(0, 4) : []);
        } else {
          const res = await ApiService.getTodoItems({
            limit: 5,
            completed: false,
            cancelled: false,
            sortBy: "createdAt",
            sortOrder: "DESC",
          });
          setData(Array.isArray(res?.data) ? res.data.slice(0, 4) : []);
        }
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [type]);

  if (loading) return <WidgetLoading />;
  if (!data.length) return <WidgetEmpty text={type === "daily" ? "Günlük rutin bulunamadı" : "Görev bulunamadı"} />;

  return (
    <div className="space-y-1.5">
      {data.map((item: any, i: number) => {
        const isCompleted = type === "daily"
          ? Boolean(item.completedToday || item.isCompleted)
          : Boolean(item.completed);
        const isToggling = type === "daily" && item.id ? togglingRoutineIds.includes(item.id) : false;
        const isTaskToggling = type === "tasks" && item.id ? togglingTaskIds.includes(item.id) : false;
        const isDisabled = isToggling || isTaskToggling || (type === "tasks" && (item.isTask || isCompleted));

        return (
        <button
          key={item.id || i}
          type="button"
          onClick={async () => {
            if (!item.id) return;

            if (type === "daily") {
              if (togglingRoutineIds.includes(item.id)) return;

              try {
                setTogglingRoutineIds((prev) => [...prev, item.id]);
                const updated = await ApiService.toggleDailyRoutineComplete(item.id);
                setData((prev) => prev.map((routine) => (routine.id === item.id ? { ...routine, ...updated } : routine)));
              } catch (error) {
                console.error("Routine toggle failed:", error);
              } finally {
                setTogglingRoutineIds((prev) => prev.filter((routineId) => routineId !== item.id));
              }

              return;
            }

            if (item.isTask || togglingTaskIds.includes(item.id)) return;

            try {
              setTogglingTaskIds((prev) => [...prev, item.id]);
              const updated = await ApiService.toggleTodoComplete(item.id);

              setData((prev) => prev.map((todo) => (todo.id === item.id ? { ...todo, ...updated } : todo)));
            } catch (error) {
              console.error("Todo toggle failed:", error);
            } finally {
              setTogglingTaskIds((prev) => prev.filter((taskId) => taskId !== item.id));
            }
          }}
          className={`flex w-full items-center gap-2 rounded-lg p-2 text-left transition-all duration-200 ${
            isDisabled ? "opacity-70 cursor-not-allowed" : "hover:bg-white/5"
          }`}
          style={{ background: `${theme.colors.primary}15` }}
          disabled={isDisabled}
        >
          <div
            className="w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-all duration-200"
            style={{
              borderColor:
                type === "tasks"
                  ? isCompleted
                    ? "#34d399"
                    : `${theme.colors.accent}60`
                  : isCompleted
                    ? "#34d399"
                    : `${theme.colors.accent}60`,
              background:
                type === "tasks"
                  ? isTaskToggling
                    ? `${theme.colors.accent}12`
                    : isCompleted
                      ? "#34d39920"
                    : "transparent"
                  : isCompleted
                    ? "#34d39920"
                    : "transparent",
            }}
          >
            {isToggling || isTaskToggling ? (
              <div className="w-2.5 h-2.5 rounded-full border border-white/30 border-t-green-400 animate-spin" />
            ) : isCompleted && (
              <div className="w-2 h-2 rounded" style={{ background: "#34d399" }} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-sm truncate transition-all duration-200 ${isCompleted ? "line-through text-gray-500" : "text-white"}`}>
              {item.text || item.title || item.name || (type === "daily" ? "Rutin" : "Görev")}
            </p>
            {type === "tasks" && (
              <p
                className={`mt-0.5 text-xs truncate ${isCompleted ? "text-green-300" : "text-gray-400"}`}
              >
                {isCompleted
                  ? "Tamamlandı"
                  : item.isTask
                  ? "Görevler modülünden"
                  : item.priority === "critical"
                    ? "Kritik"
                    : item.priority === "high"
                      ? "Yüksek"
                      : item.priority === "medium"
                        ? "Orta"
                        : item.priority === "low"
                          ? "Düşük"
                          : "Aktif"}
              </p>
            )}
          </div>
        </button>
      )})}
    </div>
  );
};

const WidgetExamCountdown: React.FC<{ theme: any }> = ({ theme }) => {
  const { user } = useAuth();
  const [exams, setExams] = useState<Array<{ id: string; name: string; date: string; departmentIds?: string[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const load = async () => {
      try {
        const res = await ApiService.getExamDates();
        const userDepartmentId = user?.department?.id || user?.departmentId;
        let userDepartmentHierarchyIds: string[] = [];

        if (userDepartmentId) {
          const visited = new Set<string>();
          let currentDepartmentId: string | null = String(userDepartmentId);

          while (currentDepartmentId && !visited.has(currentDepartmentId)) {
            visited.add(currentDepartmentId);
            userDepartmentHierarchyIds.push(currentDepartmentId);

            try {
              const department = await ApiService.getDepartment(currentDepartmentId);
              currentDepartmentId = department?.parentId ? String(department.parentId) : null;
            } catch {
              currentDepartmentId = null;
            }
          }
        }

        const all = (res?.examDates || []).filter((e) => {
          if (new Date(e.date) < new Date()) return false;
          if (!Array.isArray(e.departmentIds) || e.departmentIds.length === 0) return true;
          if (userDepartmentHierarchyIds.length === 0) return false;
          return e.departmentIds.some((departmentId) => userDepartmentHierarchyIds.includes(String(departmentId)));
        });
        all.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setExams(all.slice(0, 3));
      } catch {
        setExams([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, [user?.department?.id, user?.departmentId]);

  if (loading) return <WidgetLoading />;
  if (!exams.length) return <WidgetEmpty text="Yaklaşan sınav bulunamadı. Sistem ayarlarından ekleyebilirsiniz." />;

  const getDiff = (dateStr: string) => {
    const diff = new Date(dateStr).getTime() - now.getTime();
    if (diff <= 0) return { days: 0, hours: 0, label: "Bugün!" };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return { days, hours, label: days > 0 ? `${days} gün ${hours} saat` : `${hours} saat` };
  };

  return (
    <div className="space-y-3">
      {exams.map((exam) => {
        const diff = getDiff(exam.date);
        const urgency = diff.days <= 7 ? "#f43f5e" : diff.days <= 30 ? "#fdba74" : theme.colors.accent;
        return (
          <div key={exam.id} className="rounded-xl p-3 border" style={{ background: `${urgency}10`, borderColor: `${urgency}30` }}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm truncate">{exam.name}</p>
                <p className="text-gray-400 text-xs">{new Date(exam.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}</p>
              </div>
              <div className="flex-shrink-0 text-center">
                <div className="text-xl font-black" style={{ color: urgency }}>{diff.days > 0 ? diff.days : diff.hours}</div>
                <div className="text-xs text-gray-400">{diff.days > 0 ? "gün kaldı" : diff.hours > 0 ? "saat kaldı" : "bugün!"}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Small utility components ─────────────────────────────────────────────────

const WidgetLoading: React.FC = () => (
  <div className="flex items-center justify-center py-6">
    <div className="w-5 h-5 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
  </div>
);

const WidgetEmpty: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-center justify-center py-4">
    <p className="text-gray-500 text-sm text-center">{text}</p>
  </div>
);

// ─── Widget Card ──────────────────────────────────────────────────────────────

const WidgetCard: React.FC<{
  widgetId: WidgetId;
  theme: any;
  onRemove: () => void;
}> = ({ widgetId, theme, onRemove }) => {
  const def = WIDGET_DEFINITIONS.find((d) => d.id === widgetId);
  if (!def) return null;
  const Icon = def.icon;

  const renderContent = () => {
    switch (widgetId) {
      case "last_missing": return <WidgetLastMissing theme={theme} />;
      case "missing_summary": return <WidgetMissingSummary theme={theme} />;
      case "error_booklet": return <WidgetErrorBooklet theme={theme} />;
      case "upcoming_homework": return <WidgetCalendar theme={theme} types={["HOMEWORK"]} label="Ödev" />;
      case "upcoming_meetings": return <WidgetCalendar theme={theme} types={["MEET"]} label="Toplantı" />;
      case "upcoming_appointments": return <WidgetCalendar theme={theme} types={["APPOINTMENT"]} label="Randevu" />;
      case "upcoming_events": return <WidgetCalendar theme={theme} types={["USER", "DEPARTMENT"]} label="Etkinlik" />;
      case "daily_routine": return <WidgetTodos theme={theme} type="daily" />;
      case "tasks": return <WidgetTodos theme={theme} type="tasks" />;
      case "exam_countdown": return <WidgetExamCountdown theme={theme} />;
      default: return <WidgetEmpty text="Widget yüklenemedi" />;
    }
  };

  return (
    <div
      className="relative backdrop-blur-md rounded-2xl p-5 border transition-all duration-300 group"
      style={{ background: `linear-gradient(135deg, ${theme.colors.primary}12, ${theme.colors.secondary}08)`, borderColor: `${theme.colors.accent}25` }}
    >
      {/* Remove button */}
      <button
        onClick={onRemove}
        className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 z-10"
        style={{ background: "#f43f5e25", border: "1px solid #f43f5e50" }}
        title="Widget'ı Kaldır"
      >
        <X className="w-3 h-3 text-red-400" />
      </button>

      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${def.color}20` }}>
          <Icon className="w-4 h-4" style={{ color: def.color }} />
        </div>
        <h3 className="text-sm font-semibold text-white leading-tight pr-6">{def.label}</h3>
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
};

// ─── Widget Selector Modal ────────────────────────────────────────────────────

const WidgetSelectorModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  activeWidgets: WidgetId[];
  availableWidgets: DashboardWidgetDefinition[];
  onAdd: (id: WidgetId) => void;
  theme: any;
}> = ({ isOpen, onClose, activeWidgets, availableWidgets, onAdd, theme }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!isOpen) return null;

  const available = availableWidgets.filter((d) => !activeWidgets.includes(d.id));
  const slotsLeft = Math.max(0, MAX_WIDGETS - activeWidgets.length);

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="absolute inset-0 backdrop-blur-md"
        style={{
          background: `radial-gradient(circle at top, ${theme.colors.primary}20, transparent 42%), radial-gradient(circle at bottom right, ${theme.colors.secondary}18, transparent 32%), rgba(3, 5, 12, 0.72)`,
        }}
      />
      <div
        className="relative w-full max-w-xl max-h-[80vh] rounded-[28px] border overflow-hidden flex flex-col shadow-2xl"
        style={{
          background: `linear-gradient(160deg, ${theme.colors.background.via}f4 0%, ${theme.colors.background.base}fa 55%, ${theme.colors.background.to}fc 100%)`,
          borderColor: `${theme.colors.accent}35`,
          boxShadow: `0 30px 80px ${theme.colors.background.base}90, inset 0 1px 0 rgba(255,255,255,0.08)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-20 -right-12 h-44 w-44 rounded-full blur-3xl"
            style={{ background: `${theme.colors.primary}2e` }}
          />
          <div
            className="absolute bottom-8 -left-12 h-36 w-36 rounded-full blur-3xl"
            style={{ background: `${theme.colors.secondary}24` }}
          />
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{ background: `linear-gradient(90deg, transparent, ${theme.colors.accent}88, transparent)` }}
          />
        </div>

        {/* Header */}
        <div
          className="relative flex items-start justify-between gap-4 p-5 border-b"
          style={{ borderColor: `${theme.colors.accent}18`, background: `linear-gradient(180deg, ${theme.colors.primary}12, transparent)` }}
        >
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold tracking-[0.16em] uppercase mb-3"
              style={{ background: `${theme.colors.accent}16`, border: `1px solid ${theme.colors.accent}24`, color: theme.colors.accent }}>
              Hızlı Erişim
            </div>
            <h2 className="text-xl font-bold text-white">Hızlı Erişim Ekle</h2>
            <p className="text-sm mt-1" style={{ color: "rgba(226,232,240,0.72)" }}>
              Şu an {activeWidgets.length} Hızlı Erişim Aktif. {slotsLeft} tane daha ekleyebilirsin.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              className="hidden sm:flex items-center rounded-full px-3 py-1.5 text-xs font-semibold"
              style={{ background: `${theme.colors.primary}18`, border: `1px solid ${theme.colors.accent}22`, color: "#fff" }}
            >
              {activeWidgets.length}/{MAX_WIDGETS} aktif · {slotsLeft} hak kaldı
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <X className="w-4 h-4" style={{ color: "rgba(226,232,240,0.78)" }} />
            </button>
          </div>
        </div>

        {/* Widget list */}
        <div className="relative overflow-y-auto custom-scrollbar flex-1 p-4 sm:p-5 space-y-3">
          {available.length === 0 ? (
            <div
              className="text-center py-10 px-5 rounded-2xl border"
              style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}
            >
              <div
                className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center"
                style={{ background: `${theme.colors.primary}18`, color: theme.colors.accent }}
              >
                <Settings2 className="w-6 h-6" />
              </div>
              <p className="text-white font-semibold">
                {availableWidgets.length === 0 ? "Uygun hızlı erişim bulunamadı" : "Widget alanı dolu"}
              </p>
              <p className="text-sm mt-1" style={{ color: "rgba(226,232,240,0.68)" }}>
                {availableWidgets.length === 0
                  ? "Yetkinize uygun bir hızlı erişim bulunmuyor."
                  : "Tüm hızlı erişimler zaten eklenmiş ya da maksimum sınıra ulaşıldı."}
              </p>
            </div>
          ) : available.map((def) => {
            const Icon = def.icon;
            return (
              <button
                key={def.id}
                onClick={() => { onAdd(def.id); onClose(); }}
                className="w-full flex items-center gap-4 rounded-2xl p-4 border text-left group transition-all duration-200 hover:scale-[1.01] hover:-translate-y-0.5"
                style={{
                  background: `linear-gradient(135deg, ${def.color}10, rgba(255,255,255,0.02))`,
                  borderColor: `${def.color}22`,
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04)`,
                }}
              >
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-105"
                  style={{ background: `linear-gradient(135deg, ${def.color}28, ${def.color}14)`, boxShadow: `inset 0 1px 0 ${def.color}30` }}
                >
                  <Icon className="w-5 h-5" style={{ color: def.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-sm">{def.label}</p>
                  <p className="text-xs mt-1" style={{ color: "rgba(226,232,240,0.66)" }}>{def.description}</p>
                </div>
                <div
                  className="ml-auto flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 group-hover:scale-110"
                  style={{ background: `${def.color}14`, border: `1px solid ${def.color}26` }}
                >
                  <Plus className="w-4 h-4" style={{ color: def.color }} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (!mounted) return null;

  return createPortal(modalContent, document.body);
};

// ─── Main Dashboard Component ─────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, systemConfig } = useAuth();
  const { currentTheme } = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSeconds, setShowSeconds] = useState(false);
  const [userMetadata, setUserMetadata] = useState<any>(null);
  const [latestAnnouncements, setLatestAnnouncements] = useState<any[]>([]);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [isAnnouncementTransitioning, setIsAnnouncementTransitioning] = useState(false);
  const [cafeSettings, setCafeSettings] = useState<any>(null);
  const [localUser, setLocalUser] = useState<any>(null);

  // Widget state
  const [activeWidgets, setActiveWidgets] = useState<WidgetId[]>([]);
  const [showWidgetSelector, setShowWidgetSelector] = useState(false);
  const [widgetLoading, setWidgetLoading] = useState(true);
  const hasWidgetPermission = useCallback((permission: string | string[]) => {
    if (!Array.isArray(user?.permissions)) {
      return checkPermission(permission);
    }

    const permissions = user.permissions;

    if (Array.isArray(permission)) {
      return permission.some((item) => permissions.includes(item));
    }

    return permissions.includes(permission);
  }, [user?.permissions]);
  const permittedWidgets = useMemo(() => WIDGET_DEFINITIONS.filter((widget) => {
    const hasMenuAccess = (widget.relatedMenuItemIds || []).every((menuItemId) => {
      const permission = MENU_ITEM_PERMISSION_MAP.get(menuItemId);
      return !permission || hasWidgetPermission(permission);
    });

    const hasRequiredPermissions = (widget.requiredPermissions || []).every((permission) => hasWidgetPermission(permission));

    return hasMenuAccess && hasRequiredPermissions;
  }), [hasWidgetPermission]);
  const permittedWidgetIds = useMemo(() => new Set<WidgetId>(permittedWidgets.map((widget) => widget.id as WidgetId)), [permittedWidgets]);
  const remainingWidgets = useMemo(
    () => permittedWidgets.filter((widget) => !activeWidgets.includes(widget.id as WidgetId)),
    [activeWidgets, permittedWidgets]
  );
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);
  const yesToolsStoryItems = useMemo(() => {
    const targetIds = [
      "surveys",
      "calendar",
      "todos",
      "practice-exams",
      "cafe-menu",
      "cafe-campaigns",
    ];

    return targetIds
      .map((id) => defaultMenuItems.find((item) => item.id === id))
      .filter((item): item is NonNullable<typeof item> => Boolean(item))
      .filter((item) => !item.permission || hasWidgetPermission(item.permission))
      .map((item) => ({
        id: item.id,
        label: item.label,
        color: item.color,
        icon: item.icon,
        onClick: () => navigate(item.path),
      }))
      .concat([
        {
          id: "google-review",
          label: "Yorum Yap",
          color: currentTheme.colors.accent,
          icon: Plus,
          onClick: () => {
            window.open("https://g.page/r/CRo-tAJ41tnMEBM/review", "_blank", "noopener,noreferrer");
          },
        },
        {
          id: "google-location",
          label: "Konum",
          color: currentTheme.colors.primary,
          icon: MapPin,
          onClick: () => {
            window.open("https://maps.app.goo.gl/PGnGTcGSfqFNqCcC8", "_blank", "noopener,noreferrer");
          },
        },
      ]);
  }, [currentTheme.colors.accent, currentTheme.colors.primary, hasWidgetPermission, navigate]);
  const canAddMoreWidgets = activeWidgets.length < MAX_WIDGETS && remainingWidgets.length > 0;
  const filterPermittedWidgetIds = useCallback(
    (widgets: unknown) => normalizeWidgetIds(widgets).filter((widgetId) => permittedWidgetIds.has(widgetId)),
    [permittedWidgetIds]
  );

  // Load cafe settings
  useEffect(() => {
    ApiService.getCafePublicSettings().then(setCafeSettings).catch(console.error);
  }, []);

  // Cafe helpers
  const cleanPhone = (phone: string) => {
    if (!phone) return "";
    let c = phone.replace(/\D/g, "");
    if (c.startsWith("90")) c = c.substring(2);
    if (!c.startsWith("0")) c = "0" + c;
    return c;
  };

  const getCafeUserGroup = () => {
    if (!cafeSettings || !displayUser) return "Akademi Öğrencilerimiz";
    const uid = displayUser.departmentId ? String(displayUser.departmentId) : null;
    const unId = cafeSettings.cafeUnregisteredStudentDepartmentId ? String(cafeSettings.cafeUnregisteredStudentDepartmentId) : null;
    const gId = cafeSettings.cafeGuestUserDepartmentId ? String(cafeSettings.cafeGuestUserDepartmentId) : null;
    if (uid && unId && uid === unId) return "Öğrenciler";
    if (uid && gId && uid === gId) return "Mezunlar";
    return "Akademi Öğrencilerimiz";
  };

  const isCafeActive = cafeSettings && (cafeSettings.cafeUnregisteredStudentDepartmentId || cafeSettings.cafeGuestUserDepartmentId);

  // Clock
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e: any) => { if (typeof e?.detail === "boolean") setShowSeconds(e.detail); };
    window.addEventListener("timeFormatChanged", handler);
    return () => window.removeEventListener("timeFormatChanged", handler);
  }, []);

  // User & widgets
  useEffect(() => {
    const load = async () => {
      try {
        const me = await ApiService.getMe();
        setLocalUser(me);
        setUserMetadata(me?.metadata || null);

        const prefs = me?.preferences || {};
        const storedWidgets = filterPermittedWidgetIds(prefs.dashboardWidgets);
        const normalizedWidgets =
          storedWidgets.length > 0 ? storedWidgets : getDefaultWidgetIds(permittedWidgetIds);
        setActiveWidgets(normalizedWidgets);

        const rawWidgets = normalizeWidgetIds(prefs.dashboardWidgets);
        const shouldSyncWidgets =
          rawWidgets.length === 0
            ? normalizedWidgets.length > 0
            : rawWidgets.length !== normalizedWidgets.length ||
              rawWidgets.some((item: unknown, index: number) => item !== normalizedWidgets[index]);

        if (shouldSyncWidgets) {
          await ApiService.updateDashboardWidgets(normalizedWidgets);
        }
      } catch (e) {
        console.error("Failed to fetch user metadata:", e);
      } finally {
        setWidgetLoading(false);
      }
    };
    load();
  }, [filterPermittedWidgetIds]);

  // Announcements
  useEffect(() => {
    if (!checkPermission("announcement_list")) return;
    ApiService.getLatestAnnouncement().then((res) => {
      if (Array.isArray(res)) setLatestAnnouncements(res);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (latestAnnouncements.length <= 1) { setCurrentAnnouncementIndex(0); return; }
    let tid: any;
    const iv = setInterval(() => {
      setIsAnnouncementTransitioning(true);
      tid = setTimeout(() => {
        setCurrentAnnouncementIndex((p) => (p + 1) % latestAnnouncements.length);
        setIsAnnouncementTransitioning(false);
      }, 300);
    }, 5000);
    return () => { clearInterval(iv); if (tid) clearTimeout(tid); };
  }, [latestAnnouncements.length, currentAnnouncementIndex]);

  // Widget actions
  const saveWidgets = useCallback(async (widgets: WidgetId[]) => {
    try {
      await ApiService.updateDashboardWidgets(filterPermittedWidgetIds(widgets));
    } catch (e) {
      console.error("Widget kaydetme hatası:", e);
    }
  }, [filterPermittedWidgetIds]);

  const handleAddWidget = useCallback((id: WidgetId) => {
    if (!permittedWidgetIds.has(id) || activeWidgets.length >= MAX_WIDGETS || activeWidgets.includes(id)) return;
    const next = filterPermittedWidgetIds([...activeWidgets, id]);
    setActiveWidgets(next);
    saveWidgets(next);
  }, [activeWidgets, filterPermittedWidgetIds, permittedWidgetIds, saveWidgets]);

  const handleRemoveWidget = useCallback((id: WidgetId) => {
    const next = filterPermittedWidgetIds(activeWidgets.filter((w) => w !== id));
    setActiveWidgets(next);
    saveWidgets(next);
  }, [activeWidgets, filterPermittedWidgetIds, saveWidgets]);

  const formatDateTime = () => {
    const opts: Intl.DateTimeFormatOptions = {
      timeZone: "Europe/Istanbul", weekday: "long", hour: "2-digit", minute: "2-digit", hour12: false,
      ...(showSeconds ? { second: "2-digit" } : {}),
    };
    return new Intl.DateTimeFormat("tr-TR", opts).format(currentTime);
  };

  const getUserName = () => (user?.firstName || "Kullanıcı");

  const displayUser = localUser || user;
  const userPhone = displayUser?.gsmNumber;

  return (
    <div className="text-white relative z-[10] px-3 xs:px-4 sm:px-6 md:px-10 lg:px-24 py-5 sm:py-6 md:py-10 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="text-sm text-gray-400 mb-2">{formatDateTime()}</div>
        <div>
          <h1
            className="text-4xl lg:text-5xl font-bold mb-2 bg-clip-text text-transparent transition-all duration-300"
            style={{ backgroundImage: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary}, ${currentTheme.colors.accent})` }}
          >
            Merhaba {getUserName()}!
          </h1>
          <p className="text-xl text-gray-300">{systemConfig?.companyName || "Yeni Eğitim Sistemi"} platformuna hoş geldiniz.</p>
          {userMetadata?.schoolNumber && <p className="text-xl text-gray-300">Okul Numaranız: {userMetadata.schoolNumber}</p>}
        </div>
      </div>

      {/* Cafe card */}
      {isCafeActive && userPhone && (
        <div className="mb-8">
          <div className="backdrop-blur-md rounded-2xl p-6 shadow-lg border-2"
            style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary}1a, ${currentTheme.colors.secondary}0d)`, borderColor: `${currentTheme.colors.primary}40` }}>
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="bg-white p-4 rounded-xl shadow-inner">
                <Barcode value={cleanPhone(userPhone)} width={2} height={60} fontSize={14} background="transparent" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold text-white mb-2">Cafe Kampanyaları</h3>
                <p className="text-gray-300">Bu barkodu kasiyere göstererek{" "}
                  <span className="font-bold text-white" style={{ color: currentTheme.colors.accent }}>{getCafeUserGroup()}</span>
                  {" "}için olan kampanyalardan faydalanabilirsiniz.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isYesToolsDomain && yesToolsStoryItems.length > 0 && (
        <div className="mb-8">
          <div
            className="rounded-2xl px-3 py-4 sm:px-4 border backdrop-blur-md"
            style={{
              background: `linear-gradient(135deg, ${currentTheme.colors.primary}10, ${currentTheme.colors.secondary}08)`,
              borderColor: `${currentTheme.colors.accent}22`,
            }}
          >
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {yesToolsStoryItems.map((item) => {
                const Icon = item.icon;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={item.onClick}
                    className="group w-[72px] sm:w-[84px] min-w-0 flex flex-col items-center text-center"
                  >
                    <div
                      className="mb-2 w-14 h-14 sm:w-16 sm:h-16 rounded-full p-[2px] transition-transform duration-200 group-hover:scale-105"
                      style={{
                        background: `linear-gradient(135deg, ${item.color}, ${currentTheme.colors.accent})`,
                        boxShadow: `0 8px 22px ${item.color}24`,
                      }}
                    >
                      <div className="w-full h-full rounded-full bg-[#0f172a] border border-white/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: item.color }} />
                      </div>
                    </div>
                    <p className="text-[11px] sm:text-xs font-medium text-gray-200 leading-tight line-clamp-2">
                      {item.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Payment warning */}
      {user?.paymentStatus === 0 && user?.lastPaymentDate && (
        <div className="mb-8">
          <div className="backdrop-blur-md bg-red-500/10 border-2 border-red-500/50 rounded-2xl p-6 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-red-400 mb-2">Ödeme Yapılması Gerekiyor</h3>
                <p className="text-gray-300 mb-3">
                  Hesabınızın ödeme durumu aktif değil. Lütfen{" "}
                  <strong className="text-red-400">{Math.ceil((new Date(user.lastPaymentDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} gün</strong>
                  {" "}içinde ödemenizi gerçekleştirin. Aksi takdirde hesabınız{" "}
                  <strong className="text-red-400">{new Date(user.lastPaymentDate).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}</strong>
                  {" "}tarihinde otomatik olarak pasife alınacak ve sisteme girişiniz engellenecektir.
                </p>
                <p className="text-sm text-gray-400">Ödeme işlemleri için lütfen sistem yöneticiniz ile iletişime geçiniz.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Announcements (MUST NOT CHANGE POSITION / DESIGN) ── */}
      {checkPermission("announcement_list") && latestAnnouncements && latestAnnouncements.length > 0 && (
        <div className="mb-8">
          <div
            className={`backdrop-blur-md rounded-2xl p-8 transition-all duration-500 relative ${isAnnouncementTransitioning ? "opacity-0 scale-95 translate-y-4" : "opacity-100 scale-100 translate-y-0"}`}
            style={{ background: `linear-gradient(to right, ${currentTheme.colors.secondary}1a, ${currentTheme.colors.accent}0d, ${currentTheme.colors.primary}1a)`, border: `1px solid ${currentTheme.colors.accent}33` }}
          >
            {latestAnnouncements.length > 1 && (
              <>
                <button
                  onClick={() => { if (isAnnouncementTransitioning) return; setIsAnnouncementTransitioning(true); setTimeout(() => { setCurrentAnnouncementIndex((p) => (p - 1 + latestAnnouncements.length) % latestAnnouncements.length); setIsAnnouncementTransitioning(false); }, 300); }}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 hover:opacity-100 opacity-60 hover:scale-110 z-10"
                  style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary}40, ${currentTheme.colors.accent}40)`, border: `1px solid ${currentTheme.colors.accent}50` }}
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: currentTheme.colors.accent }} />
                </button>
                <button
                  onClick={() => { if (isAnnouncementTransitioning) return; setIsAnnouncementTransitioning(true); setTimeout(() => { setCurrentAnnouncementIndex((p) => (p + 1) % latestAnnouncements.length); setIsAnnouncementTransitioning(false); }, 300); }}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 hover:opacity-100 opacity-60 hover:scale-110 z-10"
                  style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary}40, ${currentTheme.colors.accent}40)`, border: `1px solid ${currentTheme.colors.accent}50` }}
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: currentTheme.colors.accent }} />
                </button>
              </>
            )}
            <div className="text-center px-4 sm:px-8">
              <h3
                className="text-2xl sm:text-3xl font-bold mb-4 bg-clip-text text-transparent transition-all duration-300"
                style={{ backgroundImage: `linear-gradient(to right, ${currentTheme.colors.secondary}, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})` }}
              >
                {latestAnnouncements[currentAnnouncementIndex].title}
              </h3>
              <div className="text-gray-300 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed prose prose-invert prose-lg max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    h1: ({ children }) => <h1 className="text-2xl font-bold text-white mt-4 mb-2">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-xl font-semibold text-white mt-4 mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg font-semibold text-white mt-4 mb-2">{children}</h3>,
                    p: ({ children }) => <p className="mb-2 last:mb-0 text-gray-300 whitespace-pre-wrap">{children}</p>,
                    code: ({ children, className }) => className ? (
                      <code className="bg-gray-800 text-gray-100 px-2 py-1 rounded text-sm">{children}</code>
                    ) : (
                      <code className="bg-gray-700 text-gray-200 px-1 py-0.5 rounded text-sm">{children}</code>
                    ),
                    pre: ({ children }) => <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto text-sm mb-2">{children}</pre>,
                    ul: ({ children }) => <ul className="list-disc ml-6 mb-2 text-gray-300">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal ml-6 mb-2 text-gray-300">{children}</ol>,
                    li: ({ children }) => <li className="mb-1 whitespace-pre-wrap">{children}</li>,
                    a: ({ children, href }) => <a href={href} className="underline font-semibold" style={{ color: "#ffffff", wordBreak: "break-word", overflowWrap: "break-word" }} target="_blank" rel="noopener noreferrer">{children}</a>,
                    strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    blockquote: ({ children }) => <blockquote className="border-l-4 pl-4 italic text-gray-400 my-2" style={{ borderColor: currentTheme.colors.accent }}>{children}</blockquote>,
                  }}
                >
                  {latestAnnouncements[currentAnnouncementIndex].message}
                </ReactMarkdown>
              </div>
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex flex-col sm:flex-row items-center justify-between max-w-3xl mx-auto gap-3 sm:gap-4">
                  {latestAnnouncements[currentAnnouncementIndex].creator && (
                    <div className="flex items-center gap-2 order-1 sm:order-1">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium overflow-hidden" style={{ backgroundColor: currentTheme.colors.accent }}>
                        {latestAnnouncements[currentAnnouncementIndex].creator.media?.url ? (
                          <img src={latestAnnouncements[currentAnnouncementIndex].creator.media.url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <>
                            {(latestAnnouncements[currentAnnouncementIndex].creator.firstName || "").charAt(0)}
                            {(latestAnnouncements[currentAnnouncementIndex].creator.lastName || "").charAt(0)}
                          </>
                        )}
                      </div>
                      <span className="text-sm text-gray-400">
                        {`${latestAnnouncements[currentAnnouncementIndex].creator.firstName || ""} ${latestAnnouncements[currentAnnouncementIndex].creator.lastName || ""}`.trim()}
                      </span>
                    </div>
                  )}
                  {latestAnnouncements.length > 1 && (
                    <div className="flex items-center gap-2 order-2 sm:order-2">
                      {latestAnnouncements.map((_, idx) => (
                        <div key={idx} className="h-1.5 rounded-full transition-all duration-300"
                          style={{ width: idx === currentAnnouncementIndex ? "32px" : "8px", backgroundColor: idx === currentAnnouncementIndex ? currentTheme.colors.accent : `${currentTheme.colors.accent}40` }} />
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 order-3 sm:order-3">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-400">
                      {new Date(latestAnnouncements[currentAnnouncementIndex].createdAt).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Widget Area ── */}
      <div className="mb-8">
        {/* Widget area header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${currentTheme.colors.primary}25` }}>
              <Settings2 className="w-4 h-4" style={{ color: currentTheme.colors.accent }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Hızlı Erişim</h2>
              <p className="text-xs text-gray-500">
                {activeWidgets.length}/{MAX_WIDGETS} Hızlı Erişim Aktif
              </p>
            </div>
          </div>
          {canAddMoreWidgets && (
            <button
              onClick={() => setShowWidgetSelector(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: `linear-gradient(135deg, ${currentTheme.colors.primary}30, ${currentTheme.colors.accent}20)`,
                border: `1px solid ${currentTheme.colors.accent}40`,
                color: currentTheme.colors.accent,
              }}
            >
              <Plus className="w-4 h-4" />
              Hızlı Erişim Ekle
            </button>
          )}
        </div>

        {/* Widgets grid */}
        {widgetLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse rounded-2xl h-40" style={{ background: `${currentTheme.colors.primary}10` }} />
            ))}
          </div>
        ) : activeWidgets.length === 0 ? (
          // Empty state
          <div
            className="rounded-2xl border-2 border-dashed p-10 text-center transition-all duration-300 cursor-pointer hover:scale-[1.01]"
            style={{ borderColor: `${currentTheme.colors.accent}30`, background: `${currentTheme.colors.primary}08` }}
            onClick={() => {
              if (remainingWidgets.length > 0) {
                setShowWidgetSelector(true);
              }
            }}
          >
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${currentTheme.colors.primary}20` }}>
              {remainingWidgets.length > 0 ? (
                <Plus className="w-8 h-8" style={{ color: currentTheme.colors.accent }} />
              ) : (
                <Settings2 className="w-8 h-8" style={{ color: currentTheme.colors.accent }} />
              )}
            </div>
            <h3 className="text-white font-bold text-lg mb-2">Hızlı Erişim</h3>
            <p className="text-gray-400 text-sm max-w-xs mx-auto">
              {remainingWidgets.length > 0
                ? "Eğitim durumunuz, takvim etkinlikleri, sınav geri sayımı ve daha fazlası için widget ekleyin."
                : "Yetkinize uygun hızlı erişim bulunmuyor."}
              <br /><span className="text-xs">
                {remainingWidgets.length > 0 ? `En fazla ${MAX_WIDGETS} widget ekleyebilirsiniz.` : "Yetkileriniz değiştiğinde burada otomatik görünecektir."}
              </span>
            </p>
            {remainingWidgets.length > 0 && (
              <button
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                  color: "white",
                }}
              >
                <Plus className="w-4 h-4" />
                İlk Widget'ınızı Ekleyin
              </button>
            )}
          </div>
        ) : (
          <div className={`grid gap-4 ${activeWidgets.length === 1 ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : activeWidgets.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
            {activeWidgets.map((wId) => (
              <WidgetCard
                key={wId}
                widgetId={wId}
                theme={currentTheme}
                onRemove={() => handleRemoveWidget(wId)}
              />
            ))}
            {/* Add more button — shown inline only if there is space */}
            {canAddMoreWidgets && (
              <button
                onClick={() => setShowWidgetSelector(true)}
                className="rounded-2xl border-2 border-dashed p-6 text-center transition-all duration-300 hover:scale-[1.02] flex flex-col items-center justify-center gap-3 group"
                style={{ borderColor: `${currentTheme.colors.accent}25`, background: `${currentTheme.colors.primary}06` }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" style={{ background: `${currentTheme.colors.primary}20` }}>
                  <Plus className="w-5 h-5" style={{ color: currentTheme.colors.accent }} />
                </div>
                <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">Hızlı Erişim Ekle</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Widget Selector Modal */}
      <WidgetSelectorModal
        isOpen={showWidgetSelector}
        onClose={() => setShowWidgetSelector(false)}
        activeWidgets={activeWidgets}
        availableWidgets={permittedWidgets}
        onAdd={handleAddWidget}
        theme={currentTheme}
      />
    </div>
  );
};

export default Dashboard;
